/**
 * lib/cambridgeRegistration.ts
 * ------------------------------------------------------------------
 * The candidate file for Cambridge Direct (3 Nov 2026): the Examinations
 * Unit asked for a list of candidates, with everything Cambridge needs, in
 * the .csv that Direct's `Add Candidates from Spreadsheet' screen takes.
 *
 * The format is from the Cambridge Guide to Making Entries (the March 2021 and
 * March 2023 editions, section 1.2, agree):
 *   - these 11 headings, in this order. "Unused headings ... cannot be
 *     removed", so an empty column stays in the file;
 *   - only Name, Gender and the School/Private Indicator are required;
 *   - candidate numbers are unique, 0001 to 9999, and cannot be changed after
 *     the entry is submitted;
 *   - a name is at most 60 characters including spaces, with no commas;
 *   - a date of birth is dd/mm/yyyy;
 *   - no syllabus information: subjects go in afterwards, on Prepare Entries.
 *
 * The guide's one example row shows `P' for a private candidate. `S' for a
 * school candidate is INFERRED from that: check it once against the template
 * Direct offers, along with the heading spelling (CAMBRIDGE_HEADINGS).
 *
 * What is printed on a certificate is what goes in this file, so nothing here
 * guesses quietly. A row Cambridge would refuse is held back with its reason;
 * a row it would take, but someone should look at, carries a warning.
 * ------------------------------------------------------------------
 */
import type { Student } from '@/types';

/** One constant, so a change on Direct's template is a change in one place. */
export const CAMBRIDGE_HEADINGS = [
  'Candidate Number',
  'CID UCI',
  'National ID',
  'ULN',
  'Name',
  'Gender',
  'Date of Birth',
  'Previous Centre Number',
  'Previous Candidate Number',
  'First Language English?',
  'School/Private Indicator',
] as const;

export const NAME_MAX = 60;
export const NATIONAL_ID_MAX = 20;

export type EnglishFirst = 'Y' | 'N';

/** What the office types for one candidate; `reportExamRegistrations.candidates`. */
export interface RegistrationEntry {
  number?: string;
  /** The name as it should print, when the pupil's record will not do. */
  name?: string;
  firstLanguageEnglish?: EnglishFirst;
  previousCentre?: string;
  previousCandidate?: string;
}

/** `reportExamRegistrations/{sitting}`: one document per sitting. */
export interface ExamRegistration {
  sitting: string;
  classIds: string[];
  /** Pupils in a ticked class who are not being entered. */
  excluded: string[];
  candidates: Record<string, RegistrationEntry>;
  updatedBy: string;
  updatedAt: string;
}

export type SittingMonth = 'June' | 'November';
const MONTH_NUMBER: Record<SittingMonth, string> = { June: '06', November: '11' };

/** "2027-06" for June 2027: the document's id. */
export const sittingId = (month: SittingMonth, year: number): string => `${year}-${MONTH_NUMBER[month]}`;

export function sittingTitle(id: string): string {
  const [year, month] = id.split('-');
  return `${month === '06' ? 'June' : 'November'} ${year}`;
}

/** The next sitting still ahead: entries for a sitting are made months before it. */
export function nextSitting(today: Date): { month: SittingMonth; year: number } {
  const m = today.getMonth() + 1;
  if (m < 6) return { month: 'June', year: today.getFullYear() };
  if (m < 11) return { month: 'November', year: today.getFullYear() };
  return { month: 'June', year: today.getFullYear() + 1 };
}

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

function realDate(day: number, month: number, year: number): string | null {
  const d = new Date(Date.UTC(year, month - 1, day));
  if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) return null;
  return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
}

/**
 * dd/mm/yyyy from the forms a date of birth is held in, or null. A date with
 * slashes or dashes and the year last is read day first: this is a Botswana
 * school, and that is how the forms are filled in. A two-digit year is refused,
 * since a certificate should not rest on a guess at the century.
 */
export function formatDob(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const s = raw.trim();
  let m = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(?:[T ].*)?$/.exec(s);
  if (m) return realDate(+m[3], +m[2], +m[1]);
  m = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/.exec(s);
  if (m) return realDate(+m[1], +m[2], +m[3]);
  m = /^(\d{1,2})(?:st|nd|rd|th)?[\s-]+([A-Za-z]+)[\s,-]+(\d{4})$/.exec(s);
  if (m) {
    const month = MONTHS.indexOf(m[2].slice(0, 3).toLowerCase());
    return month < 0 ? null : realDate(+m[1], month + 1, +m[3]);
  }
  return null;
}

/** "5" -> "0005"; null if it cannot be a candidate number. */
export function normaliseNumber(raw: string): string | null {
  const s = raw.trim();
  if (!/^\d{1,4}$/.test(s)) return null;
  const n = Number(s);
  return n >= 1 && n <= 9999 ? String(n).padStart(4, '0') : null;
}

/** Y or N only when the home language settles it; blank is allowed by Cambridge. */
export function englishFirstFrom(homeLanguage: string | undefined): EnglishFirst | '' {
  const words = (homeLanguage ?? '').toLowerCase().split(/[^a-z]+/).filter((w) => w && w !== 'and');
  if (!words.length) return '';
  if (words.includes('english')) return words.length === 1 ? 'Y' : '';
  return 'N';
}

export interface CandidateRow {
  studentId: string;
  /** The 11 cells, in heading order. */
  cells: string[];
  /** Why Cambridge would refuse the row. A row with any is not in the file. */
  holds: string[];
  /** Goes in the file, but someone should look. */
  warnings: string[];
}

export type RegistrationPupil = Pick<Student, 'id' | 'fullName' | 'gender' | 'dateOfBirth'> & {
  admissions?: Partial<NonNullable<Student['admissions']>>;
};

/**
 * The name to print. A surname and first name held separately give "First
 * Surname". Otherwise the record's full name goes as it stands: a first name
 * cannot be picked out of it safely, so the office is told to check the order.
 */
function nameOf(p: RegistrationPupil, entry: RegistrationEntry | undefined, warnings: string[]): string {
  const typed = entry?.name?.trim();
  if (typed) return typed;
  const first = p.admissions?.firstName?.trim();
  const surname = p.admissions?.surname?.trim();
  if (first && surname) return `${first} ${surname}`;
  warnings.push('Name order not confirmed: it prints as the record has it');
  return (p.fullName ?? '').trim();
}

/** One pupil's row. Duplicate numbers need the whole list: see buildRows. */
export function buildRow(p: RegistrationPupil, entry?: RegistrationEntry): CandidateRow {
  const holds: string[] = [];
  const warnings: string[] = [];

  let name = nameOf(p, entry, warnings).toLocaleUpperCase('en-GB');
  if (name.includes(',')) {
    name = name.replace(/\s*,\s*/g, ' ').trim();
    warnings.push('Comma removed from the name: Cambridge does not allow them');
  }
  name = name.replace(/\s+/g, ' ');
  if (!name) holds.push('No name');
  else if (name.length > NAME_MAX) holds.push(`Name is ${name.length} characters; Cambridge allows ${NAME_MAX}. Type a shorter one`);
  if (/[^ -~]/.test(name)) warnings.push('Name has an accented or special character: check it uploads correctly');

  const gender = p.gender === 'M' || p.gender === 'F' ? p.gender : '';
  if (!gender) holds.push('No gender on the record');

  const rawDob = p.dateOfBirth ?? p.admissions?.dateOfBirth;
  const dob = formatDob(rawDob);
  if (!rawDob) warnings.push('No date of birth');
  else if (!dob) warnings.push('Date of birth on the record could not be read');

  let number = '';
  if (entry?.number?.trim()) {
    const n = normaliseNumber(entry.number);
    if (n) number = n;
    else holds.push('Candidate number must be 0001 to 9999');
  } else {
    warnings.push('No candidate number');
  }

  const a = p.admissions;
  const nationalId = (a?.nationalId || a?.passportNumber || a?.idPassportNumber || '').trim();
  const idTooLong = nationalId.length > NATIONAL_ID_MAX;
  if (idTooLong) warnings.push(`ID number is over ${NATIONAL_ID_MAX} characters, so it is left out`);

  const previousCentre = (entry?.previousCentre ?? '').trim().toUpperCase();
  const rawPrevious = (entry?.previousCandidate ?? '').trim();
  const previousCandidate = normaliseNumber(rawPrevious) ?? rawPrevious;
  if (!!previousCentre !== !!previousCandidate) warnings.push('Previous entry needs both the centre and the candidate number');

  const english = entry?.firstLanguageEnglish ?? englishFirstFrom(a?.homeLanguage);

  return {
    studentId: p.id,
    cells: [number, '', idTooLong ? '' : nationalId, '', name, gender, dob ?? '', previousCentre, previousCandidate, english, 'S'],
    holds,
    warnings,
  };
}

/** Every row, with a number given to two candidates held back on both. */
export function buildRows(pupils: RegistrationPupil[], candidates: Record<string, RegistrationEntry>): CandidateRow[] {
  const rows = pupils.map((p) => buildRow(p, candidates[p.id]));
  const seen = new Map<string, number>();
  for (const r of rows) if (r.cells[0]) seen.set(r.cells[0], (seen.get(r.cells[0]) ?? 0) + 1);
  for (const r of rows) if ((seen.get(r.cells[0]) ?? 0) > 1) r.holds.push(`Candidate number ${r.cells[0]} is given to more than one pupil`);
  return rows;
}

const quote = (v: string) => (/[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);

/** The file: every heading, the rows with nothing holding them back, CRLF, no BOM. */
export function candidateCsv(rows: CandidateRow[]): string {
  const lines = [[...CAMBRIDGE_HEADINGS] as string[], ...rows.filter((r) => !r.holds.length).map((r) => r.cells)];
  return lines.map((cells) => cells.map(quote).join(',')).join('\r\n') + '\r\n';
}

/**
 * Numbers for the listed pupils who have none, in the order given, using the
 * lowest numbers free. A number already given is left alone, and is never given
 * again, even if its pupil has since been taken off the list.
 */
export function numberInOrder(
  orderedIds: string[],
  candidates: Record<string, RegistrationEntry>,
): Record<string, RegistrationEntry> {
  const used = new Set(Object.values(candidates).map((c) => (c.number ? normaliseNumber(c.number) : null)).filter(Boolean));
  const next = { ...candidates };
  let n = 1;
  for (const id of orderedIds) {
    if (next[id]?.number?.trim()) continue;
    while (used.has(String(n).padStart(4, '0'))) n++;
    if (n > 9999) break;
    const number = String(n).padStart(4, '0');
    used.add(number);
    next[id] = { ...next[id], number };
  }
  return next;
}
